import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from './logging.service';

/**
 * Global Angular error handler — captures unhandled client-side errors and
 * user-activity exceptions the framework would otherwise only print to the
 * console, and forwards them to LoggingService (→ POST /api/client-logs →
 * server structured log → ELK/OpenSearch).
 *
 * LoggingService is resolved lazily via the Injector because ErrorHandler is
 * constructed very early in bootstrap, before the HTTP stack is ready — eager
 * injection would create a circular/ordering dependency.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    constructor(private injector: Injector) {}

    handleError(error: any): void {
        // HTTP errors are already captured (with correlation) by LoggingInterceptor;
        // skip them here to avoid double-logging.
        const isHttpError = error instanceof HttpErrorResponse
            || error?.rejection instanceof HttpErrorResponse;

        if (!isHttpError) {
            try {
                const logging = this.injector.get(LoggingService);
                const err = error?.rejection ?? error;
                logging.error(err?.message || 'Unhandled client error', {
                    name: err?.name,
                    stack: typeof err?.stack === 'string' ? err.stack : undefined,
                    context: { source: 'GlobalErrorHandler' },
                });
            } catch {
                /* never let logging failure mask the original error */
            }
        }

        // Preserve default behaviour: still surface the error to the console.
        console.error(error);
    }
}
